import MeetingDetailsDateComp from "@/pages/meetings/meeting-details/meeting-details-date-comp";
import MeetingDetailsLocationComp from "@/pages/meetings/meeting-details/meeting-details-location-comp";
import MeetingDetailsParticipantsComp from "@/pages/meetings/meeting-details/meeting-details-participants-comp";
import { Button, toast } from "@heroui/react";
import { ArrowLeft, Check, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useSession } from "@clerk/react";
import { updateMeet as updateMeetApi } from "@/api/meets";

const SECTIONS = {
  date: {
    title: "Edit Date & Time",
    Comp: MeetingDetailsDateComp,
  },
  participants: {
    title: "Edit Participants",
    Comp: MeetingDetailsParticipantsComp,
  },
  location: {
    title: "Edit Location",
    Comp: MeetingDetailsLocationComp,
  },
};

const EditMeetsDetailsComp = ({ section, record, onCancel, onSaved }) => {
  const { session } = useSession();
  const [draft, setDraft] = useState(record);
  const [saving, setSaving] = useState(false);

  const activeSection = useMemo(() => SECTIONS[section], [section]);

  function updateDraft(patch) {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  function handleCancel() {
    onCancel?.();
  }

  async function handleSave() {
    const memberIds = (draft.members ?? []).map((m) => (typeof m === "string" ? m : m.member_id));

    if (section === "participants" && memberIds.length === 0) {
      toast.info("Missing Participants");
      return;
    }

    setSaving(true);
    try {
      const token = await session.getToken();

      const hostId = record?.created_by?.user_id;
      const members = hostId && !memberIds.includes(hostId) ? [...memberIds, hostId] : memberIds;
      const payload = { ...draft, members };

      const _resp = await updateMeetApi(payload, token);
   
      if (_resp.data.Result !== "SUCCESS") {
        throw new Error(_resp.data?.Message || "Update failed");
      }
      toast.success("Meeting updated");
      onSaved?.(_resp.data.Record ?? payload);
    } catch (err) {
      console.error(err);
      toast.danger("Couldn't save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  }


  if (!activeSection) return null;

  const { title, Comp } = activeSection;

  return (
    <div className="flex flex-col gap-8 h-[70vh]">
      <Button isIconOnly className="icon-button size-9" onPress={handleCancel} isDisabled={saving}>
        <ArrowLeft className="size-4 text-[#5C5C5C] dark:text-white" />
      </Button>

      <main className="grow flex flex-col gap-4">
        <span className="font-extrabold text-[22px]">{title}</span>

        <div className="flex flex-col gap-6">
          <Comp value={draft} onChange={updateDraft} />
        </div>
      </main>

      <div className="grid grid-cols-2 gap-2">
        <Button
          onPress={handleCancel}
          isDisabled={saving}
          className={"bg-white/60 dark:bg-white text-[#5855A0] rounded-2xl"}
          fullWidth
        >
          <X size={16} className="size-4" />
          Cancel
        </Button>

        <Button onPress={handleSave} isPending={saving} fullWidth className={"rounded-2xl"}>
          <Check size={16} className="size-4" /> Save
        </Button>
      </div>
    </div>
  );
};

export default EditMeetsDetailsComp;